import { PALETTE_GROUNDS, readableOn, type Brand } from "@how/website-core";
import { PALETTE_INK, TYPEFACE_STACKS } from "../look-values";
import { useBrand } from "../brand-queries";
import { useTenantRecord } from "../../tenant/queries";

/**
 * A miniature of one of the church's pages in a browser frame: its address in the bar, the site's ground, ink, accent
 * and typeface, the page's title as its headline. Drawn from the same values the site uses, so the cards look like the
 * church's own website. Decorative: the card around it names the page in text.
 */
export function SiteThumb({
  brand,
  title,
  address,
  churchName,
  variant = "page",
}: {
  readonly brand: Brand | undefined;
  readonly title: string;
  readonly address: string;
  readonly churchName: string;
  /** "page" draws a headline and text lines; "home" adds a picture band, as most home pages lead with one. */
  readonly variant?: "page" | "home";
}) {
  const ground = brand === undefined ? "#f5f5f7" : PALETTE_GROUNDS[brand.palette];
  const ink = brand === undefined ? "#17171a" : PALETTE_INK[brand.palette];
  const accent = brand?.accent ?? "#1b4d3e";
  const font = brand === undefined ? undefined : TYPEFACE_STACKS[brand.typeface];
  return (
    <div className="ws-thumb" aria-hidden>
      <div className="ws-thumb__bar">
        <span className="ws-thumb__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="ws-thumb__address">{address}</span>
      </div>
      <div className="ws-thumb__page" style={{ background: ground, color: ink, fontFamily: font }}>
        <div className="ws-thumb__nav">
          <span className="ws-thumb__brand">{churchName}</span>
          <span className="ws-thumb__links">
            <i style={{ background: ink }} />
            <i style={{ background: ink }} />
            <i style={{ background: ink }} />
          </span>
        </div>
        {variant === "home" ? (
          <div
            className="ws-thumb__hero"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 55%, ${ground}) 100%)`,
              color: readableOn(accent),
            }}
          >
            <span className="ws-thumb__headline">{title}</span>
          </div>
        ) : (
          <span className="ws-thumb__headline">{title}</span>
        )}
        <span className="ws-thumb__line" style={{ background: ink }} />
        <span className="ws-thumb__line ws-thumb__line--short" style={{ background: ink }} />
        <span className="ws-thumb__button" style={{ background: accent }} />
      </div>
    </div>
  );
}

/** `SiteThumb` in the church's own look, read here so a screen that shows none does not ask for it. */
export function ChurchSiteThumb(props: {
  readonly title: string;
  readonly address: string;
  readonly variant?: "page" | "home";
}) {
  const brand = useBrand();
  const tenant = useTenantRecord();
  return (
    <SiteThumb
      {...props}
      brand={brand.data?.brand}
      churchName={tenant.data?.name ?? "Your church"}
    />
  );
}
